import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-full text-sm border duration-200 transition-all border-transparent font-medium whitespace-nowrap transition-all outline-none disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "group bg-foreground text-background border-[1.5px] border-foreground hover:opacity-80 font-medium",
        brand: "group bg-brand text-background dark:text-foreground border-[1.5px] border-brand hover:opacity-80 font-medium",
        secondary: "group bg-secondary text-foreground border-[1.5px] border-transparent hover:bg-foreground hover:text-background font-medium",
        outline: "group bg-transparent text-foreground border-[1.5px] border-foreground hover:bg-foreground hover:text-background font-medium",
        ghost: "group bg-transparent text-foreground border-[1.5px] border-transparent hover:bg-foreground hover:text-background font-medium",
      },
      size: {
        default: "h-8 px-4",
        xs: "h-6 gap-1 px-2 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 px-3 [&_svg:not([class*='size-'])]:size-4",
        lg: "h-10 px-6 [&_svg:not([class*='size-'])]:size-4",
        icon: "size-8 rounded-full [&_svg:not([class*='size-'])]:size-4",
        "icon-xs": "size-6 rounded-full [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 rounded-full",
        "icon-lg": "size-10 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
